import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import 'leaflet/dist/leaflet.css';
import { enforceIdleOnLoad } from './auth/idleSession';
import { installStaleBuildRecovery } from './utils/staleBuild';
import { syncMediaSession } from './api/tokenManager';

// After a deploy, an open tab may ask for the previous build's chunks — reload
// once onto the new build instead of showing an error.
installStaleBuildRecovery();

// A base URL configured with a trailing slash sends visitors to e.g.
// "//auth/callback" (SSO return), which matches no route and fell through to
// the home page. Collapse repeated slashes before the router reads the path.
if (/\/{2,}/.test(window.location.pathname)) {
  const { pathname, search, hash } = window.location;
  window.history.replaceState(window.history.state, '', pathname.replace(/\/{2,}/g, '/') + search + hash);
}

// Drop a session that has been idle past the tenant's timeout before anything
// renders (e.g. a tab reopened the next morning).
enforceIdleOnLoad();

// Media cookie for plain <video>/<audio>/<iframe> file loads (tokenManager.ts).
void syncMediaSession();

// A focused number field changes its value on mouse-wheel / trackpad scroll when the pointer is over it, so
// scrolling a form could silently rewrite a number (UAT DEF-228: a duration of 2 was saved as -44). Taking the focus away
// before the browser acts makes the wheel scroll the page instead, for every number field in the app.
document.addEventListener('wheel', () => {
  const el = document.activeElement;
  if (el instanceof HTMLInputElement && el.type === 'number') el.blur();
}, { passive: true });

// Dialogs close when their backdrop is clicked. Selecting text in a field (e.g. a grade) and releasing the mouse
// outside the dialog fires that click on the backdrop — the browser targets the element both ends share — and the
// dialog closed, losing the input. A click that started inside a full-screen fixed layer and ended on the layer
// itself is a drag, not a click outside: drop it, for every modal / drawer in the app. A click that starts on the
// backdrop still closes it; keyboard clicks (detail 0) are untouched.
let pointerDownTarget: Node | null = null;
document.addEventListener('mousedown', (e) => { pointerDownTarget = e.target as Node | null; }, true);
document.addEventListener('click', (e) => {
  const down = pointerDownTarget;
  pointerDownTarget = null;
  const t = e.target;
  if (e.detail === 0 || !down || !(t instanceof HTMLElement) || down === t || !t.contains(down)) return;
  if (getComputedStyle(t).position !== 'fixed') return;
  const r = t.getBoundingClientRect();
  if (r.width < window.innerWidth * 0.9 || r.height < window.innerHeight * 0.9) return;
  e.stopPropagation();
  e.preventDefault();
}, true);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <App />
)